import React, { useState, useEffect } from "react";
import { Form, Input, Button, Typography, ConfigProvider, App } from "antd";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useDispatch } from "react-redux";
import { resetPasswordWithToken } from "../../slices/authSlice";
import { PasswordRequirements } from "../../components/auth/PasswordRequirements";
import { usePasswordPolicy } from "../../hooks/usePasswordPolicy";
import LoginImage from "../../assets/login_image.webp";
import logger from "../../utils/logger";

const { Title, Paragraph } = Typography;

const ResetPasswordPage = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [form] = Form.useForm();
  const { message } = App.useApp();
  const [loading, setLoading] = useState(false);
  const [step, setStep] = useState("form"); // form | success
  const [passwordValue, setPasswordValue] = useState("");
  const { rules: passwordRules, formRules: passwordFormRules } =
    usePasswordPolicy();
  const [searchParams] = useSearchParams();

  // Get token and email from URL parameters
  const token = searchParams.get("token");
  const email = searchParams.get("email");

  // Validate that token and email are present
  useEffect(() => {
    if (!token || !email) {
      message.error({
        content: "Invalid or missing reset link. Please request a new password reset.",
        duration: 5,
      });
      setTimeout(() => {
        navigate("/forgot-password");
      }, 3000);
    }
  }, [token, email, message, navigate]);

  const onFinish = async (values) => {
    if (!token || !email) {
      message.error({
        content: "Missing token or email. Please use the link from your email.",
        duration: 3,
      });
      return;
    }

    setLoading(true);
    try {
      await dispatch(
        resetPasswordWithToken({
          email: decodeURIComponent(email),
          password: values.password,
          token,
        })
      ).unwrap();

      setStep("success");
      message.success({
        content: "Password reset successfully!",
        duration: 3,
      });
    } catch (error) {
      logger.error("Reset password error:", error);
      message.error({
        content: error || "Failed to reset password. Please try again or request a new reset link.",
        duration: 5,
      });
    } finally {
      setLoading(false);
    }
  };

  const onFinishFailed = () => {
    message.error({
      content: "Please check your inputs.",
      duration: 3,
    });
  };

  const handleBackToLogin = () => {
    navigate("/login");
  };

  return (
    <div className="flex justify-center items-center h-[100vh] w-[100vw] relative bg-white">
      <div className="absolute top-[1.625rem] left-[1.625rem] text-[#1D2939] text-[1rem] font-normal tracking-[1.2px]">
        Dash
        <span className="text-[1.25rem] font-semibold tracking-normal">ONE</span>
      </div>

      <div className="flex justify-center items-center flex-1 flex-col gap-[2rem] px-4">
        <div className="w-full max-w-[31.625rem] flex flex-col">
          {step === "success" ? (
            <div className="w-full flex flex-col gap-[68px]">
              <Title
                level={2}
                className="!m-0 !text-[30px] !font-bold !text-[rgba(0,0,0,0.85)] !leading-[40px]"
              >
                Reset password
              </Title>
              <div className="bg-[#f6ffed] border border-[#389e0d] rounded-[24px] px-[18px] py-[16px] flex items-center justify-center">
                <Paragraph className="!m-0 !text-[16px] !leading-[25px] !text-[rgba(0,0,0,0.85)] font-normal w-full">
                  Your password has been changed successfully!
                </Paragraph>
              </div>
              <div className="w-full h-[36px]">
                <Button
                  type="primary"
                  block
                  className="h-full !rounded-[24px] !text-[16px] !font-normal !leading-[24px]"
                  onClick={handleBackToLogin}
                >
                  Go to Log in
                </Button>
              </div>
            </div>
          ) : (
            <div className="w-full flex flex-col gap-[68px]">
              <Title
                level={2}
                className="!m-0 !text-[30px] !font-bold !text-[rgba(0,0,0,0.85)] !leading-[40px]"
              >
                Reset password
              </Title>

              <ConfigProvider
                theme={{
                  token: {
                    colorPrimary: "#0E49B5",
                    borderRadius: 24,
                  },
                }}
              >
                <Form
                  form={form}
                  name="reset-password"
                  onFinish={onFinish}
                  onFinishFailed={onFinishFailed}
                  autoComplete="off"
                  layout="vertical"
                  className="w-full flex flex-col gap-[24px]"
                >
                  <Form.Item
                    label={
                      <span className="text-[14px] font-medium text-[#3f3f46] leading-[24px] px-[6px]">
                        New Password
                      </span>
                    }
                    name="password"
                    rules={[
                      { required: true, message: "Please input your password!" },
                      ...passwordFormRules,
                    ]}
                    className="!mb-0"
                  >
                    <Input.Password
                      placeholder="New Password"
                      className="!rounded-[24px] !border-[#d9d9d9] !px-4 !py-[6px] !h-auto"
                      style={{ fontSize: "14px", lineHeight: "22px", fontFamily: "Inter" }}
                      onChange={(e) => setPasswordValue(e.target.value)}
                    />
                  </Form.Item>
                  <PasswordRequirements
                    password={passwordValue}
                    rules={passwordRules}
                  />

                  <Form.Item
                    label={
                      <span className="text-[14px] font-medium text-[#3f3f46] leading-[24px] px-[6px]">
                        Confirm Password
                      </span>
                    }
                    name="confirmPassword"
                    dependencies={["password"]}
                    rules={[
                      {
                        required: true,
                        message: "Please confirm your password!",
                      },
                      ({ getFieldValue }) => ({
                        validator(_, value) {
                          if (!value || getFieldValue("password") === value) {
                            return Promise.resolve();
                          }
                          return Promise.reject(
                            new Error("The two passwords do not match.")
                          );
                        },
                      }),
                    ]}
                    className="!mb-0"
                  >
                    <Input.Password
                      placeholder="Confirm Password"
                      className="!rounded-[24px] !border-[#d9d9d9] !px-4 !py-[6px] !h-auto"
                      style={{
                        fontSize: "14px",
                        lineHeight: "22px",
                        fontFamily: "Inter",
                      }}
                    />
                  </Form.Item>
                </Form>
              </ConfigProvider>

              <div className="w-full h-[36px]">
                <Button
                  type="primary"
                  htmlType="submit"
                  loading={loading}
                  onClick={() => form.submit()}
                  className="w-full h-full !rounded-[24px] !text-[16px] !font-normal !leading-[24px]"
                >
                  Confirm Password
                </Button>
              </div>
            </div>
          )}
        </div>
      </div>

      <div className="absolute bottom-[2rem] left-[1.625rem] text-[rgb(71,84,103)] text-[0.75rem] font-[400]">
        @DashONE 2025
      </div>
      <div
        className="flex-1 h-full w-[50vw] self-stretch rounded-[20px_0_0_20px] bg-no-repeat bg-center bg-cover hidden md:block"
        style={{ backgroundImage: `url(${LoginImage})` }}
      />
    </div>
  );
};

export default ResetPasswordPage;
